import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ModalForm } from "@/components/ModalForm";
import { List, Users, Sparkles, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface CreateListFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, description?: string) => Promise<void>;
  isLoading: boolean;
}

export const CreateListForm = ({ isOpen, onClose, onSubmit, isLoading }: CreateListFormProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    await onSubmit(name.trim(), description.trim() || undefined);
    setName("");
    setDescription("");
  };

  return (
    <ModalForm
      title={t("lists.create", "Create List")}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={isLoading ? t("lists.form.creating", "Creating...") : t("lists.form.create", "Create List")}
      cancelText={t("common.cancel", "Cancel")}
      submitDisabled={!name.trim() || isLoading}
      className="max-w-2xl"
    >
      {/* Visual Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-sm mb-4">
          <List className="h-8 w-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t("lists.create", "Create List")}
        </h3>
        <p className="text-gray-600 text-sm">
          {t("lists.form.subtitle", "Organize your candidates into custom lists for better management")}
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Name Field */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-500" />
            {t("lists.form.name", "Name")} *
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("lists.form.namePlaceholder", "Enter list name")}
            className="h-11 text-base"
            required
          />
          <p className="text-xs text-gray-500">
            {t("lists.form.nameHelp", "Choose a descriptive name for your list")}
          </p>
        </div>

        {/* Description Field */}
        <div className="space-y-2">
          <Label htmlFor="description" className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-gray-500" />
            {t("lists.form.description", "Description")}
          </Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("lists.form.descriptionPlaceholder", "Enter list description (optional)")}
            className="min-h-[100px] text-base resize-none"
            rows={4}
          />
          <p className="text-xs text-gray-500">
            {t("lists.form.descriptionHelp", "Optional description to help you remember what this list is for")}
          </p>
        </div>

        {/* Preview Card */}
        {name.trim() && (
          <Card className="border-dashed border-2 border-gray-200 bg-gray-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{name}</h4>
                  {description.trim() && (
                    <p className="text-sm text-gray-600 mt-1">{description}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {t("lists.form.preview", "0 candidates")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ModalForm>
  );
};
